






































































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import { VaultFileUsageSubjectTypeEnum } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useVaultView from "@/hooks/useVaultView";
import emitter from "@/lib/emitter";
import { isFile } from "@/lib/fileHelpers";
import { $t } from "@/lib/i18nHelpers";
import { isGetUserMediaSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { AttachmentFile, InputAttachmentFile } from "@/types";
import { EVENTS } from "@/const";
import AttachmentFilesPreview from "@/components/AttachmentFilesPreview";
import AttachmentPriceSetting from "@/components/AttachmentPriceSetting";
import AudioRecordingController from "@/components/AudioRecordingController";
import AudioRecordingView from "@/components/AudioRecordingView";
import BaseIconButton from "@/components/BaseIconButton";
import BaseInputFileWithPreview from "@/components/BaseInputFileWithPreview";
import BaseSendMessage from "@/components/BaseSendMessage";
import TipController from "@/components/TipController";
import TipIcon from "@/components/TipIcon";
import VaultModal from "@/components/VaultModal";

export default defineComponent({
  name: "MessageSendWithAttachmentView",
  components: {
    BaseInputFileWithPreview,
    VaultModal,
    BaseSendMessage,
    BaseIconButton,
    AttachmentFilesPreview,
    AttachmentPriceSetting,
    TipController,
    TipIcon,
    AudioRecordingController,
    AudioRecordingView,
  },
  props: {
    sending: { type: Boolean, required: true },
    packageList: { type: Array as () => PaymentPackage[], required: true },
    files: { type: Array as () => AttachmentFile[], default: () => [] },
    attachmentPriceId: { type: String, required: true },
    recipientIds: { type: Array as () => string[], required: true },
    threadId: { type: String, default: "0" },
    uploadingAttachment: { type: Boolean, required: true },
    isIncomingMessagesAllow: { type: Boolean, default: false },
    priceAmount: { type: String },
    allowedPublicFilesCount: { type: Number, default: 0 },
    allowedPrivateFilesCount: { type: Number, default: 1 },
  },
  setup(props, { emit }) {
    const { authUserStrict, userVerificationStatus } = useAuthUser();
    const value = ref<string>("");
    const isAudioRecording = ref(false);
    const hasText = computed(() => value.value.trim().length > 0);
    const hasAttachment = computed(() => props.files && props.files.length > 0);
    const isNoPrice = computed(() => props.attachmentPriceId === "0");
    const isAudioMessageEnabled = config.featureAudioMessageAttachments;
    const isAudioMessage = computed(() => props.files.length === 1 && props.files[0].type.startsWith("audio"));
    const canTip = computed<boolean>(() => config.featureTippingMessages && !hasText.value && !hasAttachment.value);
    const loadAttachIconColor = computed(() =>
      props.allowedPrivateFilesCount === 0 ? "var(--palette-gray-lighten-6)" : "var(--palette-gray)"
    );
    const handleInput = (v: string) => {
      value.value = v;
      emit("input", v);
    };

    const onRecordStarted = () => {
      isAudioRecording.value = true;
      emit("audioRecordingStarted");
    };
    const onRecordStopped = () => {
      isAudioRecording.value = false;
      emit("audioRecordingStopped");
    };

    const checkOnApproved = (id: string) => {
      if (props.files && props.files.length > 0 && userVerificationStatus.value === UserVerificationStatus.VERIFIED) {
        emit("setPrice", id);
        return;
      }
      emit("setPrice", "0");
      emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: "Price setup declined: your account is pending approval" });
    };

    const allowSend = (value: string) => {
      return !props.sending && !props.uploadingAttachment && (hasAttachment.value || value.length > 0);
    };

    const isUploadingAvailable = (access: boolean = false) => {
      return (access && props.allowedPublicFilesCount !== 0) || (!access && props.allowedPrivateFilesCount !== 0);
    };
    const { showPrivateVault, showPublicVault, isVaultAvailable, showVaultOrNative } = useVaultView();
    const onAddFiles = (showNativeUploader: () => void, publicAccess: boolean): void => {
      if (isUploadingAvailable(publicAccess) && !isAudioRecording.value) {
        showVaultOrNative(showNativeUploader, publicAccess);
      }
    };
    const addAttachment = (files: (AttachmentFile | File)[], publicAccess: boolean) => {
      const fileList: (InputAttachmentFile | AttachmentFile)[] = files.map(file => {
        if (isFile(file)) {
          return { file, publicAccess };
        }
        file.publicAccess = publicAccess;
        return file;
      });
      fileList.length > 0 && emit("addAttachment", fileList);
    };

    const threadSubject = computed<Subject<VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD>>(() => {
      return {
        subjectId: props.threadId,
        subjectType: VaultFileUsageSubjectTypeEnum.MESSAGE_THREAD,
      };
    });

    const messagePlaceholder = computed(() => {
      if ((props.priceAmount && isAudioMessage.value) || isAudioRecording.value) return $t("Audio");
      if (props.priceAmount && hasAttachment.value) return $t("Media");
      return $t("Message");
    });

    return {
      showPrivateVault,
      showPublicVault,
      value,
      canTip,
      isNoPrice,
      userVerificationStatus,
      authUserStrict,
      hasAttachment,
      hasText,
      isVaultAvailable,
      messagePlaceholder,
      allowSend,
      handleInput,
      checkOnApproved,
      addAttachment,
      onAddFiles,
      loadAttachIconColor,
      threadSubject,
      isAudioRecording,
      isAudioMessageEnabled,
      isAudioMessage,
      onRecordStarted,
      onRecordStopped,
      isGetUserMediaSupported,
    };
  },
});
